import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../partage/api/client';
import type { CibleDuTuto, GesteDuTuto, ReponseDuTuto, VueDuTuto } from '../partage/api/types';
import type { ArriveeSurLaFeuille } from '../partage/navigationDeLaFeuille';
import { cles } from '../partage/relectures';
import { ouvrirLAide } from './aideOuverte';
import { useEcranDuTuto } from './ecranDuTuto';

/**
 * Le tuto et l'Aide (tickets 31 et 33, découpés en parties au ticket 11 de retours-testeurs),
 * côté client : demander l'étape à montrer et la liste de l'Aide, et les boutons.
 *
 * Le client ne juge rien. Il dit où il est ; le serveur répond par l'étape en cours. La
 * requête se relance à chaque changement d'écran (l'écran est dans sa clé) et après chaque
 * écriture de l'app (`main.tsx`) : c'est ainsi que le tuto « voit » un geste fait.
 */

/**
 * Le tuto a écrit en base de lui-même (un voyage de démo chargé ou supprimé) : tout ce qui
 * est à l'écran se relit, lui compris. La relecture rend `recharger: false` quand rien n'a
 * changé entre-temps : pas de boucle.
 */
export function relireSIlLeFaut(client: QueryClient, reponse: ReponseDuTuto | undefined) {
  if (reponse?.recharger) void client.invalidateQueries();
}

export function useTuto() {
  const client = useQueryClient();
  const ecran = useEcranDuTuto();
  return useQuery({
    queryKey: cles.tutoDeLEcran(ecran),
    queryFn: async () => {
      const reponse = await api.tuto(ecran);
      // Après la réponse, pas pendant : cette requête-ci est encore en cours.
      if (reponse.recharger) setTimeout(() => relireSIlLeFaut(client, reponse), 0);
      return reponse;
    },
    // Le tuto ne clignote pas entre deux écrans : il garde l'étape d'avant le temps de la réponse.
    placeholderData: keepPreviousData,
    staleTime: 0,
    // Un tuto qui ne répond pas ne doit rien empêcher : rien à l'écran, et on n'insiste pas.
    retry: false,
    // Pas d'écran connu (l'adresse « / », le temps d'être renvoyé sur l'accueil,
    // « Maintenant », `App.tsx`) : on attend d'y être. Sans ça, l'ouverture envoyait deux
    // demandes à la fois.
    enabled: ecran !== null,
  });
}

/**
 * Aller là où l'étape se voit, fiche ouverte si elle en nomme une. `enCarte` : là où une partie
 * de l'Aide commence, sur la carte, même si la feuille était ouverte dans une autre vue (ticket 17).
 */
export function useAllerA() {
  const naviguer = useNavigate();
  return (cible: CibleDuTuto, enCarte = false) => {
    const etat: ArriveeSurLaFeuille = { ...(cible.fiche ? { ouvrirLaFiche: cible.fiche } : {}), ...(enCarte ? { enCarte } : {}) };
    naviguer(cible.chemin, Object.keys(etat).length > 0 ? { state: etat } : undefined);
  };
}

/**
 * « Fais-le pour moi », « Passer », « Suivant », « Quitter », et les deux boutons de la fin.
 * `rouvrir` : ce geste ramène à la liste de l'Aide (« Retour à l'Aide », « Ouvrir l'Aide »,
 * ou « Quitter » une partie de l'Aide, comme dans la maquette).
 */
export function useGesteDuTuto() {
  const client = useQueryClient();
  const ecran = useEcranDuTuto();
  const allerA = useAllerA();

  return useMutation({
    mutationFn: ({ geste, vue }: { geste: GesteDuTuto; vue: VueDuTuto; rouvrir?: boolean }) =>
      api.gesteDuTuto(geste, vue.etape, ecran),
    onSuccess: (reponse, { geste, vue, rouvrir }) => {
      client.setQueriesData({ queryKey: cles.tuto }, reponse);
      relireSIlLeFaut(client, reponse);
      if (rouvrir) ouvrirLAide(true);
      // La partie finie ou quittée, son voyage est parti : si on le regardait, on en sort. Ou,
      // dans un niveau lancé en entier, la partie suivante commence : on va à son départ, en Carte.
      const autrePartie = reponse.tuto?.phase === 'bandeau' && vue.phase === 'bandeau' && reponse.tuto.partie !== vue.partie;
      if (reponse.aller) allerA(reponse.aller, autrePartie);
      if (geste !== 'faire' || vue.phase !== 'bandeau') return;
      // Le serveur a écrit à la place de l'utilisateur : tout ce qui est à l'écran se relit,
      // et on va voir le résultat là où le geste se fait.
      void client.invalidateQueries();
      if (vue.sorte === 'geste' && vue.cible) allerA(vue.cible);
    },
  });
}

/**
 * Lancer une partie depuis l'Aide (« La présentation » comprise), ou tout un niveau par son
 * titre (ticket 17 de retours-testeurs) : ce qui était en cours s'arrête, la partie commence à
 * sa première étape, sur un voyage de démo neuf, et on va là où elle se joue.
 */
export function useLancerPartie() {
  const client = useQueryClient();
  const ecran = useEcranDuTuto();
  const allerA = useAllerA();
  return useMutation({
    mutationFn: (quoi: string | { niveau: number }) =>
      typeof quoi === 'string'
        ? api.gesteDuTuto('lancer', null, ecran, quoi)
        : api.gesteDuTuto('lancer', null, ecran, null, quoi.niveau),
    onSuccess: (reponse) => {
      client.setQueriesData({ queryKey: cles.tuto }, reponse);
      relireSIlLeFaut(client, reponse);
      const vue = reponse.tuto;
      if (!vue) return;
      ouvrirLAide(false);
      // La visite se fait sur l'écran qu'elle présente ; une partie, là où elle commence.
      if (vue.phase === 'visite') allerA(vue.cible);
      else if (reponse.aller) allerA(reponse.aller, true);
    },
  });
}
